import { computed, ComputedRef } from 'vue';
import { useLiveQueryV2 } from '@libs/use';
import {
  SYMBOL_INSTRUMENT_TRADING_STATE_CHANNEL_PREFIX,
  SYMBOL_TRADE_CHANNEL_PREFIX,
  SYMBOL_CLOSING_PRICE_CHANNEL_PREFIX,
  SYMBOL_TRADE_CHANNEL_PRE_OPENING_PREFIX,
  transformClosingPriceChange,
  transformPreOpeningTradePriceChange,
  transformSymbolTradingStateChange,
  transformTradePriceChange,
  transformPricePercentage,
} from '@core/symbol';
import { CODES } from '@libs/use';
import { WatchlistItem, WatchlistItemsQuery } from '@libs/api-reference';

import * as API from '../requests';
import { WATCHLIST_QUERY_KEYS } from '../constants';

type UseWatchlistItemsConfig = {
  listen: boolean;
  immediate: ComputedRef<boolean>;
};

export default function useLiveWatchlistItems(
  watchlistId: ComputedRef<string>,
  { listen = true, immediate }: UseWatchlistItemsConfig
) {
  const {
    execute: getWatchlistItems,
    isPending: isLoadingWatchlistItems,
    isRevalidating: isRevalidatingWatchlistItems,
    isSuccess: isSuccessLoadingWatchlistItems,
    isError: isErrorLoadingWatchlistItems,
    result,
    status: watchlistItemsStatus,
    ...query
  } = useLiveQueryV2<WatchlistItemsQuery>({
    fetcher: () => API.getWatchlistItems(watchlistId.value),
    key: WATCHLIST_QUERY_KEYS.watchlistItems(watchlistId),
    immediate,
    getChannels: listen
      ? (result) => {
          if (result?.data.items) {
            return result.data.items.reduce(
              (symbolsChannels: string[], watchlist) => {
                symbolsChannels.push(
                  `${SYMBOL_TRADE_CHANNEL_PREFIX}${watchlist.isin}`,
                  `${SYMBOL_INSTRUMENT_TRADING_STATE_CHANNEL_PREFIX}${watchlist.isin}`,
                  `${SYMBOL_CLOSING_PRICE_CHANNEL_PREFIX}${watchlist.isin}`,
                  `${SYMBOL_TRADE_CHANNEL_PRE_OPENING_PREFIX}${watchlist.isin}`
                );

                return symbolsChannels;
              },
              []
            );
          }

          return [];
        }
      : () => {},
    onUpdate: (signal) => {
      const actions = {
        [CODES.SymbolTradePriceChangeSignal]: transformTradePriceChange,
        [CODES.SymbolStateChangeSignal]: transformSymbolTradingStateChange,
        [CODES.TseInstrumentClosingPriceChangeSignal]:
          transformClosingPriceChange,
        [CODES.SymbolTradePriceChangePreOpeningSignal]:
          transformPreOpeningTradePriceChange,
      };

      if (actions[signal.code]) {
        const data = actions[signal.code](signal.data);

        if (!result.value?.data?.items) {
          return;
        }

        const index = result.value.data.items.findIndex((s) => {
          return s.isin === data.isin;
        });

        if (index > -1) {
          const item = result.value.data.items[index];

          if (
            signal.code === CODES.SymbolTradePriceChangeSignal &&
            item.lastTradePrice !== data.lastTradePrice
          ) {
            Object.assign(item, data);
          } else if (signal.code !== CODES.SymbolTradePriceChangeSignal) {
            Object.assign(item, data);
          }
        }
      }
    },
    onSuccess(response) {
      if (response.data.items && response.data.items.length) {
        response.data.items = response.data.items.map(
          (item: WatchlistItem & { pricePercentage?: number }) => {
            transformPricePercentage(item);

            return item;
          }
        );
      }
    },
  });

  const watchListItems = computed<WatchlistItem[]>(() => {
    return result.value?.data.items || [];
  });

  return {
    getWatchlistItems,
    watchListItems,
    isLoadingWatchlistItems,
    isRevalidatingWatchlistItems,
    isSuccessLoadingWatchlistItems,
    isErrorLoadingWatchlistItems,
    watchlistItemsStatus,
    ...query,
  };
}
